import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { tokens, Page, Meta } from "../../styles";
import BibBlock from "../BibBlock";

const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const H1 = styled.h1`
  margin: 0;
  font-family: ${tokens.serif};
  font-size: 34px;
  font-weight: 500;
  line-height: 1.12;
  letter-spacing: -0.015em;
  color: ${tokens.ink};
`;
const Dek = styled.p`
  font-size: 16px;
  line-height: 1.55;
  color: ${tokens.soft};
`;

/* Optional dotted figure that sits under the title, description and date. */
const FigureWrap = styled.div`
  margin-top: 0px;
`;

const Body = styled.div`
  position: relative;
  margin-top: ${(p) => (p.$afterFigure ? "48px" : "48px")};
`;

/* Contents live in the left margin and follow the reader. */
const Side = styled.aside`
  position: absolute;
  top: 0;
  bottom: 0;
  right: calc(100% + 64px);
  width: 170px;
  @media (max-width: 1060px) { display: none; }
`;
const Toc = styled.ul`
  position: sticky;
  top: 110px;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
  font-size: 13px;
  line-height: 1.4;
`;
const TocLink = styled.a`
  position: relative;
  display: block;
  text-decoration: none;
  color: ${(p) => (p.$on ? tokens.ink : tokens.soft)};
  opacity: ${(p) => (p.$on ? 1 : 0.7)};
  transition: color 0.25s, opacity 0.25s;
  &:hover { color: ${tokens.ink}; opacity: 1; }
  &::before {
    content: "";
    position: absolute;
    left: -14px;
    top: 0.5em;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${tokens.blue};
    opacity: ${(p) => (p.$on ? 1 : 0)};
    transition: opacity 0.25s;
  }
`;

export const Prose = styled.div`
  line-height: 1.7;
  p { margin: 0 0 18px; }
  h2 {
    font-family: ${tokens.serif};
    font-size: 25px;
    font-weight: 500;
    line-height: 1.25;
    letter-spacing: -0.01em;
    color: ${tokens.ink};
    margin: 60px 0 16px;
  }
  h3 {
    font-family: ${tokens.serif};
    font-style: italic;
    font-size: 19px;
    font-weight: 400;
    line-height: 1.35;
    color: ${tokens.ink};
    margin: 34px 0 10px;
  }
  ul {
    list-style: none;
    margin: 4px 0 22px;
    padding: 0 0 0 22px;
  }
  li {
    position: relative;
    margin: 0 0 10px;
  }
  li::before {
    content: "";
    position: absolute;
    left: -17px;
    top: 0.68em;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${tokens.blue};
  }
  ul.defs li { margin-bottom: 16px; }
  .lead {
    display: block;
    font-weight: 500;
    color: ${tokens.ink};
  }
  .mail {
    margin: 4px 0 22px;
    padding: 16px 18px;
    background: ${tokens.cream2};
    border-radius: 6px;
    font-family: ${tokens.mono};
    font-size: 12.5px;
    line-height: 1.65;
    color: ${tokens.body};
    white-space: pre-wrap;
  }
  .mail .lab {
    display: block;
    color: ${tokens.soft};
  }
  .mail .ph {
    color: ${tokens.blue};
    background: rgba(58, 117, 196, 0.1);
    border-radius: 3px;
    padding: 0 3px;
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
  }
  .mail .ph.note {
    color: ${tokens.soft};
    background: rgba(107, 101, 92, 0.1);
    font-style: italic;
  }
`;

const Citation = styled.section`
  margin-top: 56px;
  padding-top: 36px;
  border-top: 1px solid ${tokens.rule};
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const H2Cite = styled.h2`
  margin: 0;
  font-family: ${tokens.serif};
  font-size: 21px;
  font-weight: 500;
  line-height: 1.3;
  color: ${tokens.ink};
`;

/* Tracks which section heading the reader has most recently passed. */
function useActiveSection(ids) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      let idx = 0;
      ids.forEach((id, i) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < 160) idx = i;
      });
      setActive(idx);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ids]);
  return active;
}

export default function PostLayout({ title, description, date, figure, sections, bibtex, children }) {
  const ids = useMemo(() => sections.map((s) => s.id), [sections]);
  const active = useActiveSection(ids);

  return (
    <Page as="article">
      <Header>
        <H1>{title}</H1>
        {description && <Dek>{description}</Dek>}
        <Meta>{date}</Meta>
      </Header>

      {figure && <FigureWrap>{figure}</FigureWrap>}

      <Body $afterFigure={Boolean(figure)}>
        <Side aria-label="Contents">
          <Toc>
            {sections.map((s, i) => (
              <li key={s.id}>
                <TocLink href={`#${s.id}`} $on={active === i} aria-current={active === i ? "location" : undefined}>
                  {s.label}
                </TocLink>
              </li>
            ))}
          </Toc>
        </Side>
        <Prose>{children}</Prose>
      </Body>

      {bibtex && (
        <Citation>
          <H2Cite>Citation</H2Cite>
          <BibBlock bibtex={bibtex} />
        </Citation>
      )}
    </Page>
  );
}